import React, { useMemo } from 'react';

/**
 * How many networks sit on each channel, stacked by security.
 *
 * The question the spectrum view was really being asked — which channels are
 * congested, and what kind of networks are on them — is one number per channel.
 * Drawing a hundred overlapping bells to convey a hundred integers made it
 * illegible at exactly the density where the answer starts to matter.
 *
 * 2.4 GHz shows all thirteen channels whether or not anything is on them,
 * because an empty channel is information: it is where you would ask the
 * customer to move. 5 GHz shows only observed channels — the band has too many
 * to enumerate, and absence there is unremarkable.
 */

const CLASSES = [
  { id: 'OPEN', label: 'Open', colour: '#fbbf24' },
  { id: '802.1X', label: 'Enterprise', colour: '#a78bfa' },
  { id: 'SAE', label: 'WPA3', colour: '#34d399' },
  { id: 'PSK', label: 'WPA2', colour: '#818cf8' },
  { id: 'OTHER', label: 'Unknown', colour: '#64748b' },
];

const classify = (authSuite) =>
  CLASSES.some((c) => c.id === authSuite) ? authSuite : 'OTHER';

const BAR_H = 76;

const ChannelOccupancy = ({ accessPoints = [] }) => {
  const bands = useMemo(() => {
    const build = (bandId, channels) => {
      const counts = {};
      channels.forEach((ch) => {
        counts[ch] = { total: 0 };
        CLASSES.forEach((c) => { counts[ch][c.id] = 0; });
      });
      accessPoints.forEach((ap) => {
        if (ap.band !== bandId || !ap.channel) return;
        if (!counts[ap.channel]) return;
        counts[ap.channel][classify(ap.auth_suite)] += 1;
        counts[ap.channel].total += 1;
      });
      return channels.map((ch) => ({ channel: ch, ...counts[ch] }));
    };

    const observed5 = [...new Set(
      accessPoints.filter((ap) => ap.band === '5' && ap.channel).map((ap) => ap.channel),
    )].sort((a, b) => a - b);

    return [
      { id: '2.4', label: '2.4 GHz', rows: build('2.4', Array.from({ length: 13 }, (_, i) => i + 1)) },
      { id: '5', label: '5 GHz', rows: build('5', observed5) },
    ];
  }, [accessPoints]);

  const peak = Math.max(
    1,
    ...bands.flatMap((b) => b.rows.map((r) => r.total)),
  );

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2 px-1">
        <span className="text-xs font-medium text-slate-400">Channel occupancy</span>
        <span className="text-[11px] text-slate-600">
          networks per channel · peak {peak}
        </span>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {bands.map((band) => (
          <div key={band.id} className="flex-1 min-w-0">
            <div className="text-[11px] text-slate-500 mb-1 px-1">{band.label}</div>

            {band.rows.length === 0 ? (
              <div
                className="flex items-center justify-center text-[11px] text-slate-700 border border-dashed border-slate-800 rounded"
                style={{ height: BAR_H + 18 }}
              >
                nothing observed on this band
              </div>
            ) : (
              <div className="flex items-end gap-[3px]" style={{ height: BAR_H + 18 }}>
                {band.rows.map((row) => (
                  <div
                    key={row.channel}
                    className="flex-1 min-w-0 flex flex-col items-center justify-end group"
                    title={`Channel ${row.channel} — ${row.total} network${row.total === 1 ? '' : 's'}`}
                  >
                    <div
                      className="w-full flex flex-col-reverse rounded-sm overflow-hidden transition-opacity group-hover:opacity-80"
                      style={{ height: (row.total / peak) * BAR_H }}
                    >
                      {CLASSES.map((c) => (
                        row[c.id] > 0 && (
                          <div
                            key={c.id}
                            style={{
                              backgroundColor: c.colour,
                              height: `${(row[c.id] / row.total) * 100}%`,
                            }}
                          />
                        )
                      ))}
                    </div>
                    <span className="text-[9px] text-slate-600 mt-1 tabular-nums">
                      {row.channel}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 px-1">
        {CLASSES.map((c) => (
          <span key={c.id} className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <span
              className="inline-block w-3 h-[3px] rounded-full"
              style={{ backgroundColor: c.colour }}
            />
            {c.label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default ChannelOccupancy;
